<div class="toolkit-modal">
  @if (!loading) {
    <div class="header-row">
      <div>
        <div class="header">{{ title }}</div>
        @if (config.developmentMode) { <span class="dev-badge">DEVELOPMENT</span> }
      </div>
      <div class="actions-row">
        @if (config.user?.token) {
          <button mat-stroked-button color="primary" [cdkCopyToClipboard]="config.user?.token ?? ''" matTooltip="Copy authentication token">
            <mat-icon>vpn_key</mat-icon> Token
          </button>
        }
        @if (config.explorerUrl) {
          <button mat-stroked-button color="primary" (click)="onOpenExplorer()" matTooltip="Open API explorer">
            <mat-icon>api</mat-icon> Explorer
          </button>
        }
        @for (action of extraActions; track action.id) {
          <button mat-stroked-button (click)="onAction(action.id)" [disabled]="action.disabled" [matTooltip]="action.tooltip ?? ''">
            @if (action.icon) { <mat-icon>{{ action.icon }}</mat-icon> } {{ action.label }}
          </button>
        }
      </div>
    </div>

    @if (fastLoginEnabled) {
      <section class="card fast-login-card">
        <div class="section-header"><span>{{ config.fastLogin?.title ?? 'Fast profile login' }}</span><mat-icon>switch_account</mat-icon></div>
        <div class="warning"><mat-icon>science</mat-icon><span>Development only. Creates a test session without the profile password.</span></div>
        @if (config.fastLogin?.loading) {
          <div class="inline-status"><mat-icon>hourglass_top</mat-icon><span>Loading test profiles…</span></div>
        }
        @if (config.fastLogin?.error) {
          <div class="inline-hint">{{ config.fastLogin?.error }}</div>
        }
        <mat-form-field class="field" appearance="outline" subscriptSizing="dynamic">
          <mat-label>Filter profiles</mat-label>
          <input matInput [(ngModel)]="profileFilter" placeholder="Name, email, or role" />
          <mat-icon matPrefix>search</mat-icon>
        </mat-form-field>
        <mat-form-field class="field" appearance="outline" subscriptSizing="dynamic">
          <mat-label>Test profile</mat-label>
          <mat-select [ngModel]="selectedProfileId" (ngModelChange)="onProfileChange($event)" [disabled]="config.fastLogin?.loading === true">
            @for (profile of filteredProfiles; track profile.id) {
              <mat-option [value]="profile.id" [disabled]="profile.disabled === true">
                {{ profileLabel(profile) }} · {{ profile.email }}
              </mat-option>
            } @empty {
              <mat-option disabled>{{ config.fastLogin?.emptyLabel ?? 'No matching profiles' }}</mat-option>
            }
          </mat-select>
          <mat-icon matPrefix>person_search</mat-icon>
        </mat-form-field>
        @if (selectedProfile; as profile) {
          <div class="profile-preview">
            <div><strong>{{ profileLabel(profile) }}</strong><span>{{ profile.email }}</span></div>
            <span class="role-list">{{ profileRoleNames(profile) || 'No roles' }}</span>
          </div>
        }
        @if (config.fastLogin?.requiresTokenSecret && !tokenSecret.trim()) {
          <div class="inline-hint">Enter the local token secret in Current session to enable fast login.</div>
        }
        <button mat-flat-button color="primary" (click)="onFastLogin()" [disabled]="fastLoginDisabled">
          <mat-icon>{{ loginPending ? 'hourglass_top' : 'login' }}</mat-icon>
          {{ loginPending ? 'Switching…' : (config.fastLogin?.buttonLabel ?? 'Login as profile') }}
        </button>
      </section>
    }

    @if (hasUserSection) {
      <section class="card">
        <div class="section-header"><span>Current session</span><button type="button" mat-icon-button (click)="jsonRequested.emit(config.user)" matTooltip="View user JSON"><mat-icon>code</mat-icon></button></div>
        <div class="user-summary"><mat-icon>account_circle</mat-icon><div><strong>{{ config.user?.firstname }} {{ config.user?.lastname }}</strong><span>{{ config.user?.email }}</span></div></div>
        @if (config.user?.token) {
          <div class="token-status"><span>{{ tokenStatus }}</span><button mat-button (click)="showTokenPayload = !showTokenPayload">{{ showTokenPayload ? 'Hide' : 'Inspect' }} JWT</button></div>
          @if (showTokenPayload) { <pre class="code-block" [cdkCopyToClipboard]="tokenPayload">{{ tokenPayload }}</pre> }
        }
        @if (config.roles?.length) {
          <mat-form-field class="field" appearance="outline" subscriptSizing="dynamic">
            <mat-label>Roles</mat-label>
            <mat-select [ngModel]="selectedRoles" (ngModelChange)="onSelectedRolesChange($event)" multiple [compareWith]="compareRoles">
              @for (role of config.roles; track role.id) { <mat-option [value]="role">{{ role.name }}</mat-option> }
            </mat-select><mat-icon matPrefix>group</mat-icon>
          </mat-form-field>
        }
        <div class="form-grid">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Expiration (unix seconds)</mat-label><input matInput type="number" [ngModel]="expirationUnix" (ngModelChange)="onExpirationChange($event)" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Token secret</mat-label><input matInput [type]="showTokenSecret ? 'text' : 'password'" [ngModel]="tokenSecret" (ngModelChange)="onTokenSecretChange($event)" />
            <button type="button" mat-icon-button matSuffix (click)="showTokenSecret = !showTokenSecret"><mat-icon>{{ showTokenSecret ? 'visibility_off' : 'visibility' }}</mat-icon></button>
          </mat-form-field>
        </div>
        <div class="preset-row"><span>Expires {{ expireDateLabel }}</span><button mat-button (click)="onPresetExpiration(1)">+1h</button><button mat-button (click)="onPresetExpiration(24)">+24h</button><button mat-button (click)="onPresetExpiration(168)">+7d</button></div>
        <button mat-flat-button color="primary" (click)="onSave()" [disabled]="saveDisabled">{{ saveButtonLabel }}</button>
      </section>
    }

    @if (config.featureFlags?.length) {
      <section class="card">
        <div class="section-header"><span>Feature flags</span><mat-icon>flag</mat-icon></div>
        @for (flag of config.featureFlags; track flag.id; let i = $index) {
          <div class="flag-row"><div><strong>{{ flag.label }}</strong>@if (flag.description) { <span>{{ flag.description }}</span> }</div><mat-checkbox [ngModel]="flag.enabled" (ngModelChange)="onFeatureFlagChange(i, $event)" [disabled]="flag.disabled === true"></mat-checkbox></div>
        }
      </section>
    }

    @if (hasEnvironmentSection) {
      <section class="card">
        <div class="section-header"><span>Environment</span><button type="button" mat-icon-button (click)="jsonRequested.emit(config.environment)" matTooltip="View JSON"><mat-icon>code</mat-icon></button></div>
        <mat-form-field class="field" appearance="outline" subscriptSizing="dynamic"><mat-label>Filter keys or values</mat-label><input matInput [(ngModel)]="environmentFilter" /><mat-icon matPrefix>search</mat-icon></mat-form-field>
        <div class="entry-grid">@for (item of filteredEnvironmentEntries; track item.key) { <div class="entry-row" [cdkCopyToClipboard]="item.value" matTooltip="Copy value"><strong>{{ item.key }}</strong><span>{{ item.value }}</span></div> }</div>
      </section>
    }

    @if (hasDiagnosticsSection) {
      <section class="card">
        <div class="section-header"><span>Runtime diagnostics</span><button type="button" mat-icon-button (click)="jsonRequested.emit(config.diagnostics)" matTooltip="View JSON"><mat-icon>code</mat-icon></button></div>
        <div class="entry-grid">@for (item of diagnosticEntries; track item.key) { <div class="entry-row" [cdkCopyToClipboard]="item.value" matTooltip="Copy value"><strong>{{ item.key }}</strong><span>{{ item.value }}</span></div> }</div>
      </section>
    }
  } @else {
    <div class="loading"><mat-icon>hourglass_empty</mat-icon><span>{{ loadingLabel }}</span></div>
  }
</div>
